import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Play, RotateCcw, Users, Hammer, Bell, Cpu, ArrowRight, Zap, Bone, Archive, ChevronsDown, Handshake, Gavel, KeyRound, Feather, PenOff, Target } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Simulator() {
  const [game, setGame] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const setup = async (specs) => {
    setBusy(true); setErr("");
    try {
      const { data } = await axios.post(`${API}/sim/new`, { players: specs });
      setGame(data);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const action = async (endpoint, body) => {
    setBusy(true); setErr("");
    try {
      const { data } = await axios.post(`${API}/sim/${game.id}${endpoint}`, body || {});
      setGame(data);
      return data;
    } catch (e) { setErr(e?.response?.data?.detail || e.message); throw e; }
    finally { setBusy(false); }
  };

  const reset = () => { setGame(null); setErr(""); };

  if (!game) return <Setup onStart={setup} busy={busy} err={err} />;

  return (
    <div className="fade-up" data-testid="simulator-view">
      <div className="flex justify-between items-center flex-wrap gap-4 mb-6">
        <div>
          <div className="eyebrow" style={{ color: "var(--crimson-deep)" }}>Playtest Simulator · Hot-Seat</div>
          <h2 className="serif text-3xl md:text-4xl font-bold mt-1" style={{ color: "#F0E6D2" }}>The Dinner Inquiry — Live</h2>
        </div>
        <button className="brass-btn flex items-center gap-2" onClick={reset} data-testid="sim-reset-btn">
          <RotateCcw size={12} /> New Game
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
        <div>
          <StatusBar game={game} />
          {game.phase === "opening" && (
            <OpeningPanel game={game} action={action} busy={busy} />
          )}
          <FloorPlan game={game} action={action} busy={busy} />
          <Rondel game={game} onTake={(idx) => action("/take", { slice_index: idx })} busy={busy} />
          <ActionRow game={game} action={action} busy={busy} />
          {err && <div className="mt-3 crimson-tag" data-testid="sim-error">{err}</div>}
        </div>
        <SideRail game={game} action={action} busy={busy} />
      </div>

      {game.phase === "game-over" && <GameOver game={game} onReset={reset} />}
      <PuzzleModal game={game} action={action} busy={busy} />
    </div>
  );
}

function PuzzleModal({ game, action, busy }) {
  const [open, setOpen] = useState(false);
  const [picks, setPicks] = useState([]);
  const active = game.players[game.active_index];
  const hasScroll = active && !active.is_ai && active.items.includes("coded-scrawl");
  const code = game.puzzle?.code || [];

  useEffect(() => { if (!hasScroll) setOpen(false); }, [hasScroll]);
  useEffect(() => { setPicks([]); }, [open]);

  if (!hasScroll) return null;

  const togglePick = (di) => {
    if (picks.includes(di)) setPicks(picks.filter(x => x !== di));
    else if (picks.length < 4) setPicks([...picks, di]);
  };

  const submit = () => {
    if (picks.length !== 4) return;
    const pips = picks.map(i => active.dominoes[i]);
    action("/puzzle", { player_id: active.id, pips }).then(() => setOpen(false));
  };

  return (
    <>
      <button
        className="brass-btn brass-btn-solid fixed bottom-6 right-6 flex items-center gap-2 z-40"
        onClick={() => setOpen(true)}
        data-testid="open-puzzle-btn"
      >
        <KeyRound size={14} /> Dominoglyph
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="puzzle-modal">
          <div className="paper rounded-md p-8 max-w-lg w-full">
            <div className="flex justify-between items-start">
              <div>
                <div className="eyebrow" style={{ color: "var(--crimson-deep)" }}>Code Pad · Snooze the Alarm</div>
                <h3 className="serif text-2xl font-bold mt-1" style={{ color: "var(--paper-ink)" }}>Enter the Dominoglyph</h3>
              </div>
              <button onClick={() => setOpen(false)} className="mono text-sm" style={{ color: "var(--paper-ink)" }} data-testid="puzzle-close">✕</button>
            </div>

            <p className="serif-italic mt-2" style={{ color: "var(--paper-ink)" }}>
              Match all four top-side pips to snooze the alarm 6 ticks. Two or three matches ease it partially. Miss and the alarm ticks up.
            </p>

            <div className="mt-4">
              <div className="eyebrow mb-2" style={{ color: "var(--crimson-deep)" }}>Target Code (top-side pips)</div>
              <div className="flex gap-2">
                {code.map((c, i) => (
                  <div key={i} className="flex-1 rounded p-3 text-center serif font-bold text-2xl" style={{ background: "rgba(197,160,89,0.15)", border: "2px solid var(--brass)", color: "var(--paper-ink)" }} data-testid={`code-slot-${i}`}>
                    {c}
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-4">
              <div className="eyebrow mb-2" style={{ color: "var(--crimson-deep)" }}>Your Hand — tap 4 dominoes in order</div>
              <div className="flex flex-wrap gap-2">
                {active.dominoes.map((d, di) => {
                  const idx = picks.indexOf(di);
                  return (
                    <button
                      key={di}
                      onClick={() => togglePick(di)}
                      style={{
                        background: idx >= 0 ? "rgba(230,161,53,0.35)" : "transparent",
                        padding: "6px",
                        borderRadius: "5px",
                        border: idx >= 0 ? "2px solid var(--brass)" : "2px solid transparent",
                        cursor: "pointer",
                        position: "relative",
                      }}
                      data-testid={`puzzle-dom-${di}`}
                    >
                      <DominoTile total={d} size="lg" />
                      {idx >= 0 && (
                        <span
                          className="mono"
                          style={{
                            position: "absolute", top: "-6px", right: "-6px",
                            background: "var(--brass)", color: "#0E0A08",
                            width: "18px", height: "18px", borderRadius: "50%",
                            fontSize: "10px", display: "flex", alignItems: "center", justifyContent: "center",
                            fontWeight: 700,
                          }}
                        >
                          {idx + 1}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="mono text-xs mt-2" style={{ color: "var(--paper-ink)" }}>
                Picked: {picks.map(i => active.dominoes[i]).join(" · ") || "—"}
              </div>
            </div>

            <button
              className="brass-btn brass-btn-solid w-full mt-6"
              onClick={submit}
              disabled={busy || picks.length !== 4}
              data-testid="puzzle-submit-btn"
            >
              Enter Code (spends these 4 dominoes)
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// ============ DOMINO VISUAL ============
// Deterministically derive two halves (0..6 each) from a pip total (0..12)
function dominoHalves(total) {
  const t = Math.max(0, Math.min(12, total | 0));
  // Prefer a specific canonical mapping so each total renders the same each time
  const a = Math.min(6, t);
  const b = t - a;
  // Balance the halves toward "middle" for a more classic-looking tile
  if (a - b >= 2) return [a - 1, b + 1];
  return [a, b];
}

// pip positions on a 3x3 grid, expressed as [col, row] with 0..2 both axes
const PIP_POSITIONS = {
  0: [],
  1: [[1, 1]],
  2: [[0, 0], [2, 2]],
  3: [[0, 0], [1, 1], [2, 2]],
  4: [[0, 0], [2, 0], [0, 2], [2, 2]],
  5: [[0, 0], [2, 0], [1, 1], [0, 2], [2, 2]],
  6: [[0, 0], [2, 0], [0, 1], [2, 1], [0, 2], [2, 2]],
};

function DominoTile({ total, size = "sm", "data-testid": testId }) {
  const [a, b] = dominoHalves(total);
  const dims = size === "lg"
    ? { w: 64, h: 32, pip: 3.5, pad: 6 }
    : { w: 44, h: 22, pip: 2.4, pad: 4 };  const halfW = (dims.w - 2) / 2;
  const renderHalf = (val, xOffset) => {
    const positions = PIP_POSITIONS[val] || [];
    return positions.map(([col, row], i) => {
      const cx = xOffset + dims.pad + col * ((halfW - dims.pad * 2) / 2);
      const cy = dims.pad + row * ((dims.h - dims.pad * 2) / 2);
      return <circle key={i} cx={cx} cy={cy} r={dims.pip} fill="#1A0F08" />;
    });
  };
  return (
    <svg
      width={dims.w}
      height={dims.h}
      viewBox={`0 0 ${dims.w} ${dims.h}`}
      data-testid={testId}
      style={{
        filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.5))",
        display: "block",
      }}
    >
      {/* tile body */}
      <rect
        x="0.5" y="0.5" width={dims.w - 1} height={dims.h - 1}
        rx="2.5"
        fill="#F3ECE0"
        stroke="#8B6E3F"
        strokeWidth="1"
      />
      {/* divider */}
      <line
        x1={dims.w / 2} y1="3"
        x2={dims.w / 2} y2={dims.h - 3}
        stroke="#8B6E3F"
        strokeWidth="0.8"
      />
      {renderHalf(a, 0)}
      {renderHalf(b, dims.w / 2)}
    </svg>
  );
}

function FaceDownTile({ size = "sm", label = "?", "data-testid": testId }) {
  const dims = size === "lg" ? { w: 64, h: 32 } : { w: 44, h: 22 };
  return (
    <svg width={dims.w} height={dims.h} viewBox={`0 0 ${dims.w} ${dims.h}`} data-testid={testId}
      style={{ filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.5))", display: "block" }}>
      <rect x="0.5" y="0.5" width={dims.w - 1} height={dims.h - 1} rx="2.5"
        fill="#6B2C2C" stroke="#3E1616" strokeWidth="1" />
      <text x={dims.w / 2} y={dims.h / 2 + (size === "lg" ? 4 : 3)} textAnchor="middle"
        fontSize={size === "lg" ? 12 : 9} fontFamily="serif" fill="#F0E6D2" fontStyle="italic">
        {label}
      </text>
    </svg>
  );
}

// ============ OPENING CASCADE PANEL (§2 The Chief's Pen) ============
function OpeningPanel({ game, action, busy }) {
  const cursor = game.opening_cursor;
  const player = game.players[cursor];
  const lastIdx = (game.players.length - 1) % game.players.length;
  const isLast = cursor === lastIdx;
  const hasZero = player.dominoes.includes(0);
  const [pickIdx, setPickIdx] = useState(null);
  useEffect(() => { setPickIdx(null); }, [cursor]);

  const kick = (endpoint, body) => action(endpoint, body);

  return (
    <div className="leather rounded-md p-5 mb-5 border" style={{ borderColor: "var(--brass)" }} data-testid="opening-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="eyebrow" style={{ color: "var(--brass)" }}>§2 · The Chief's Pen</div>
          <div className="serif text-2xl font-bold" style={{ color: "#F0E6D2" }}>
            {isLast && !hasZero ? "Chief, I forgot a pen!" : (hasZero ? "Point blank!" : "Forced Trade Cascade")}
          </div>
          <div className="serif-italic text-sm mt-1" style={{ color: "var(--paper-warm)" }}>
            {hasZero
              ? `${player.name} holds [0|0]. Play it in the Kitchen to open portals + take the blank-check bonus.`
              : isLast
                ? `${player.name} is last in rotation — no trade. Pick any tile to play face-down as [0|0].`
                : `${player.name} discards one tile to the Spoliation Pile → draws from the Bone Bag.`}
          </div>
        </div>
        <div className="text-right space-y-1">
          <div className="mono text-xs" style={{ color: "var(--brass-dim)" }}>🦴 Bone Bag: <strong style={{ color: "var(--brass)" }}>{game.bone_bag_remaining}</strong></div>
          <div className="mono text-xs" style={{ color: "var(--crimson)" }}>🗂 Spoliation: <strong>{game.spoliation_pile.length}</strong></div>
        </div>
      </div>

      {/* Kitchen chain visualization (empty pre-play) */}
      {game.chain.length > 0 && (
        <div className="mb-3 p-3 rounded" style={{ background: "rgba(15,10,7,0.4)", border: "1px solid var(--border-brass)" }}>
          <div className="eyebrow mb-2" style={{ color: "var(--brass-dim)" }}>Kitchen anchor chain</div>
          <div className="flex gap-2 flex-wrap items-center">
            {game.chain.map((t, i) => (
              t.face_down
                ? <FaceDownTile key={i} size="lg" label="✒" data-testid={`chain-tile-${i}`} />
                : <DominoTile key={i} total={t.a + t.b} size="lg" data-testid={`chain-tile-${i}`} />
            ))}
          </div>
        </div>
      )}

      {/* Cursor player hand + actions */}
      {player.is_ai ? (
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Cpu size={14} style={{ color: "var(--brass)" }} className="animate-pulse" />
            <span className="mono text-sm" style={{ color: "var(--paper-warm)" }}>
              {player.name} is deciding…
            </span>
          </div>
          <button
            className="brass-btn brass-btn-solid"
            onClick={() => kick("/opening/ai-step", {})}
            disabled={busy}
            data-testid="opening-ai-step-btn"
          >
            {busy ? "…" : `Auto-resolve ${player.name}`} <ArrowRight size={12} className="inline ml-1" />
          </button>
        </div>
      ) : hasZero ? (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex gap-1.5 items-center">
            {player.dominoes.map((d, di) => (
              <DominoTile key={di} total={d} size={d === 0 ? "lg" : "sm"} data-testid={`opening-hand-${di}`} />
            ))}
          </div>
          <button
            className="brass-btn brass-btn-solid flex items-center gap-2"
            style={{ background: "linear-gradient(180deg, var(--brass), #7A5A2A)" }}
            onClick={() => kick("/opening/point-blank", { player_id: player.id })}
            disabled={busy}
            data-testid="opening-point-blank-btn"
          >
            <Feather size={14} /> Yell “Point Blank!” — play [0|0]
          </button>
        </div>
      ) : (
        <div>
          <div className="mono text-xs mb-2" style={{ color: "var(--muted)" }}>
            Pick a tile from your hand to {isLast ? "play FACE-DOWN as [0|0]" : "discard to Spoliation"}:
          </div>
          <div className="flex gap-2 flex-wrap items-center mb-3">
            {player.dominoes.map((d, di) => (
              <button
                key={di}
                onClick={() => setPickIdx(di)}
                className="p-1 rounded transition-all"
                style={{
                  background: pickIdx === di ? "rgba(230,161,53,0.25)" : "transparent",
                  outline: pickIdx === di ? "2px solid var(--brass)" : "1px dashed var(--border-brass)",
                }}
                data-testid={`opening-pick-${di}`}
              >
                <DominoTile total={d} size="lg" />
              </button>
            ))}
          </div>
          <button
            className="brass-btn brass-btn-solid flex items-center gap-2"
            onClick={() => {
              if (pickIdx == null) return;
              if (isLast) kick("/opening/forgot-pen", { player_id: player.id, domino_index: pickIdx });
              else kick("/opening/trade", { player_id: player.id, domino_index: pickIdx });
            }}
            disabled={busy || pickIdx == null}
            data-testid={isLast ? "opening-forgot-pen-btn" : "opening-trade-btn"}
          >
            {isLast
              ? <><PenOff size={14} /> Cry “Chief, I forgot a pen!” — play face-down</>
              : <><ChevronsDown size={14} /> Discard & draw from Bone Bag</>}
          </button>
        </div>
      )}

      {/* Point-token roster reminder */}
      {game.players.some(p => p.point_token) && (
        <div className="mt-3 pt-3 border-t text-xs mono" style={{ borderColor: "var(--border-brass)", color: "var(--paper-warm)" }}>
          🎯 Point held by: {game.players.filter(p => p.point_token).map(p => p.name).join(", ")}
        </div>
      )}
    </div>
  );
}

// ============ SETUP ============
function ringDinnerBell() {
  // 808-style cowbell: two square waves at inharmonic ratios through a bandpass filter,
  // fast metallic decay, struck twice for "clonk-clonk".
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const now = ctx.currentTime;

    const strike = (offset, gainScale = 1) => {
      // Bandpass filter shapes the metallic tone
      const filter = ctx.createBiquadFilter();
      filter.type = "bandpass";
      filter.frequency.value = 800;
      filter.Q.value = 3.5;

      const env = ctx.createGain();
      env.gain.setValueAtTime(0.0001, now + offset);
      env.gain.exponentialRampToValueAtTime(0.55 * gainScale, now + offset + 0.003);
      env.gain.exponentialRampToValueAtTime(0.0001, now + offset + 0.35);

      // Two square oscillators at 808-cowbell frequencies
      [800, 540].forEach((freq) => {
        const osc = ctx.createOscillator();
        osc.type = "square";
        osc.frequency.value = freq;
        osc.connect(filter);
        osc.start(now + offset);
        osc.stop(now + offset + 0.4);
      });

      filter.connect(env).connect(ctx.destination);
    };

    strike(0, 1.0);
    strike(0.18, 0.8);  // second clonk slightly softer

    setTimeout(() => ctx.close && ctx.close(), 900);
  } catch (e) {
    // silent fail — audio may be blocked or unsupported
  }
}

function Setup({ onStart, busy, err }) {
  const [players, setPlayers] = useState([
    { name: "Ada", is_ai: false },
    { name: "Ben", is_ai: true },
    { name: "Cass", is_ai: true },
  ]);

  const update = (i, patch) => setPlayers(players.map((p, idx) => idx === i ? { ...p, ...patch } : p));
  const add = () => players.length < 5 && setPlayers([...players, { name: `Player ${players.length + 1}`, is_ai: true }]);
  const remove = (i) => players.length > 2 && setPlayers(players.filter((_, idx) => idx !== i));

  return (
    <div className="fade-up max-w-2xl mx-auto" data-testid="sim-setup">
      <div className="eyebrow mb-2" style={{ color: "var(--crimson-deep)" }}>Playtest Simulator · Seat the Table</div>
      <h2 className="serif text-3xl md:text-4xl font-bold mt-1 mb-2" style={{ color: "#F0E6D2" }}>Who's at Dinner?</h2>
      <p className="serif-italic text-lg mb-6" style={{ color: "var(--paper-warm)" }}>
        2 to 5 seats. Mark any as AI — Claude Sonnet 4.6 will play them.
      </p>

      <div className="leather rounded-md p-6 space-y-3">
        {players.map((p, i) => (
          <div key={i} className="flex items-center gap-3" data-testid={`player-row-${i}`}>
            <span className="eyebrow w-6" style={{ color: "var(--brass)" }}>{i + 1}.</span>
            <input
              className="input-field flex-1"
              value={p.name}
              onChange={(e) => update(i, { name: e.target.value })}
              data-testid={`player-name-${i}`}
            />
            <label className="flex items-center gap-2 mono text-xs uppercase tracking-widest cursor-pointer" style={{ color: p.is_ai ? "var(--brass)" : "var(--muted)" }}>
              <input
                type="checkbox"
                checked={p.is_ai}
                onChange={(e) => update(i, { is_ai: e.target.checked })}
                data-testid={`player-ai-${i}`}
              />
              AI
            </label>
            {players.length > 2 && (
              <button className="brass-btn text-xs px-2" onClick={() => remove(i)} data-testid={`player-remove-${i}`}>×</button>
            )}
          </div>
        ))}
        {players.length < 5 && (
          <button className="brass-btn w-full flex items-center justify-center gap-2 mt-2" onClick={add} data-testid="add-player-btn">
            <Users size={12} /> Add Player
          </button>
        )}
      </div>

      <button
        className="brass-btn brass-btn-solid w-full mt-6 flex items-center justify-center gap-2 text-base"
        onClick={() => { ringDinnerBell(); onStart(players); }}
        disabled={busy}
        data-testid="start-game-btn"
      >
        <Play size={14} /> {busy ? "Setting the table…" : "Ring the Dinner Bell"}
      </button>
      {err && <div className="mt-3 crimson-tag">{err}</div>}
    </div>
  );
}

// ============ STATUS BAR ============
function StatusBar({ game }) {
  return (
    <>
      <div className="leather rounded-md p-4 mb-4 flex flex-wrap gap-6 items-center" data-testid="status-bar">
        <div>
          <div className="eyebrow">Shift</div>
          <div className="serif text-2xl font-bold" style={{ color: "var(--brass)" }}>
            {game.shift} <span className="text-sm" style={{ color: "var(--muted)" }}>/ {game.max_shift} slices: {game.shift_slices_taken}</span>
            {game.overtime && <span className="crimson-tag ml-2">Overtime x2</span>}
          </div>
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="eyebrow flex items-center gap-1"><Bell size={10} /> Alarm Meter</div>
          <div className="mt-1 h-3 rounded-full" style={{ background: "#0F0A07", border: "1px solid var(--border-brass)" }}>
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${(game.alarm / game.alarm_cap) * 100}%`,
                background: game.alarm > game.alarm_cap * 0.75
                  ? "linear-gradient(90deg, #A63232, #E88A8A)"
                  : "linear-gradient(90deg, #C5A059, #E6A135)",
              }}
              data-testid="alarm-fill"
            />
          </div>
          <div className="mono text-xs mt-1" style={{ color: "var(--muted)" }}>{game.alarm} / {game.alarm_cap}</div>
        </div>
        <div>
          <div className="eyebrow">Active</div>
          <div className="serif text-xl font-bold flex items-center gap-2" style={{ color: "#F0E6D2" }} data-testid="active-player">
            {game.players[game.active_index].name}
            {game.players[game.active_index].is_ai && <Cpu size={14} style={{ color: "var(--brass)" }} />}
          </div>
        </div>
        {game.ragnar?.active && (
          <div>
            <div className="eyebrow flex items-center gap-1">🐾 Ragnar</div>
            <div className="serif text-lg font-bold" style={{ color: "#F0B84E" }} data-testid="ragnar-room">
              {game.ragnar.room}
            </div>
          </div>
        )}
      </div>
      {game.contest && (
        <div className="mb-4 p-3 rounded" style={{ background: "rgba(166,50,50,0.14)", border: "1px solid var(--crimson)" }} data-testid="contest-banner">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="crimson-tag"><Gavel size={10} className="inline mr-1" /> Contest Open</span>
            <span className="mono text-sm" style={{ color: "#F0E6D2" }}>
              {game.rondel[game.contest.slice_index].icon} {game.rondel[game.contest.slice_index].name} — provisionally held by <strong style={{ color: "var(--brass)" }}>{game.players.find(p => p.id === game.contest.claimant_id)?.name}</strong>. Any non-active player with 2+ dominoes may up the ante.
            </span>
          </div>
        </div>
      )}
    </>
  );
}

// ============ FLOOR PLAN — Blueprint (matches reference maps) ============

// Grid: 24 columns × 10 rows. Rooms as {x, y, w, h} in grid cells.
const GROUND_ROOMS = [
  { id: "kitchen",  name: "KITCHEN (0,0)", x: 0,  y: 0, w: 5,  h: 4, portal: { x: 4.5, y: 3.5, label: "P (pantry)" }, anchor: true },
  { id: "pantry",   name: "PANTRY",        x: 5,  y: 2, w: 3,  h: 2 },
  { id: "garage",   name: "DOUBLE GARAGE", x: 0,  y: 4, w: 7,  h: 4, portal: { x: 6.5, y: 4.5, label: "P" } },
  { id: "banquet",  name: "BANQUET HALL",  x: 7,  y: 0, w: 7,  h: 3 },
  { id: "lounge",   name: "GRANDE LOUNGE", x: 7,  y: 3, w: 8,  h: 5 },
  { id: "scrip",    name: "SCRIPTORIUM",   x: 14, y: 0, w: 5,  h: 4, trapdoor: true },
  { id: "bed1",     name: "BEDROOM 1",     x: 10, y: 6, w: 3,  h: 4, portal: { x: 10.5, y: 6.5, label: "P (BIR)" } },
  { id: "bed2",     name: "BEDROOM 2",     x: 13, y: 6, w: 3,  h: 4, portal: { x: 13.5, y: 6.5, label: "P (BIR)" } },
  { id: "bed3",     name: "BEDROOM 3",     x: 16, y: 6, w: 3,  h: 4, portal: { x: 16.5, y: 6.5, label: "P (BIR)" } },
  { id: "master",   name: "MASTER 5×6",    x: 19, y: 4, w: 5,  h: 6, portal: { x: 19.5, y: 4.5, label: "P (BIR)" } },
  { id: "ensuite",  name: "ENSUITE",       x: 20, y: 3, w: 4,  h: 1, small: true },
  { id: "bath",     name: "BATH",          x: 14, y: 4, w: 2,  h: 2, small: true },
];

const BASEMENT_ROOMS = [
  { id: "foyer",    name: "THE FOYER",  x: 1,  y: 2, w: 6,  h: 6, note: "A smaller elegant room", palms: true, portal_arrival: { x: 4, y: 5 } },
  { id: "vault",    name: "THE VAULT",  x: 7,  y: 0, w: 15, h: 10, note: "Very large temperature-controlled room", vault: true },
];

const GRID_COLS = 24;
const GRID_ROWS = 10;

function FloorPlan({ game, action, busy }) {
  const [floor, setFloor] = useState("ground");
  const [availableTargets, setAvailableTargets] = useState([]);
  const active = game.players[game.active_index];

  // Auto-follow the active player's floor
  useEffect(() => {
    const isBasement = BASEMENT_ROOMS.some(r => r.id === active.room);
    setFloor(isBasement ? "basement" : "ground");
  }, [active.room]);

  useEffect(() => {
    if (active.is_ai || game.phase === "game-over") { setAvailableTargets([]); return; }
    axios.get(`${API}/sim/${game.id}/moves/${active.id}`)
      .then(r => setAvailableTargets(r.data.targets || []))
      .catch(() => setAvailableTargets([]));
  }, [active.id, active.room, game.phase, game.id, active.is_ai]);

  const rooms = floor === "ground" ? GROUND_ROOMS : BASEMENT_ROOMS;
  const cell = 34;
  const pad = 22;
  const W = GRID_COLS * cell + pad * 2;
  const H = GRID_ROWS * cell + pad * 2;

  const targetIds = new Set(availableTargets.map(t => t.id));
  const canAct = !active.is_ai && game.phase !== "game-over" && game.phase !== "opening";

  const onRoomClick = (roomId) => {
    if (!canAct || busy) return;
    if (roomId === active.room) return;
    if (!targetIds.has(roomId)) return;
    action("/move", { player_id: active.id, room: roomId });
  };

  const onTileClick = (tile) => {
    if (!canAct || busy || tile.revealed) return;
    if (tile.room !== active.room) return;
    action("/reveal", { tile_id: tile.id, player_id: active.id });
  };

  const playersByRoom = {};
  for (const p of game.players) {
    if (!playersByRoom[p.room]) playersByRoom[p.room] = [];
    playersByRoom[p.room].push(p);
  }

  return (
    <div className="mb-4" data-testid="floor-plan">
      <div className="flex justify-between items-center mb-2">
        <div className="eyebrow">Blueprint · {floor === "ground" ? "Ground Floor" : "Underground Level"}</div>
        <div className="flex gap-1">
          <button
            className={`brass-btn text-xs ${floor === "ground" ? "brass-btn-solid" : ""}`}
            onClick={() => setFloor("ground")}
            data-testid="floor-ground-btn"
          >Ground</button>
          <button
            className={`brass-btn text-xs ${floor === "basement" ? "brass-btn-solid" : ""}`}
            onClick={() => setFloor("basement")}
            data-testid="floor-basement-btn"
          >Basement</button>
        </div>
      </div>

      <div
        className="rounded-md overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #0F2A4A 0%, #0A1E36 100%)",
          border: "1px solid #2A4E7A",
          boxShadow: "0 8px 30px rgba(0,0,0,0.6), inset 0 0 60px rgba(255,255,255,0.03)",
        }}
        data-testid={`floor-svg-${floor}`}
      >
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: "block" }}>
          <g stroke="rgba(255,255,255,0.08)" strokeWidth="0.5">
            {Array.from({ length: GRID_COLS + 1 }).map((_, i) => (
              <line key={`v${i}`} x1={pad + i * cell} y1={pad} x2={pad + i * cell} y2={pad + GRID_ROWS * cell} />
            ))}
            {Array.from({ length: GRID_ROWS + 1 }).map((_, i) => (
              <line key={`h${i}`} x1={pad} y1={pad + i * cell} x2={pad + GRID_COLS * cell} y2={pad + i * cell} />
            ))}
          </g>

          {rooms.map(r => {
            const rx = pad + r.x * cell;
            const ry = pad + r.y * cell;
            const rw = r.w * cell;
            const rh = r.h * cell;
            const isCurrent = active.room === r.id;
            const isTarget = targetIds.has(r.id);
            const clickable = canAct && (isTarget || isCurrent);
            const viaPortal = availableTargets.find(t => t.id === r.id)?.via_portal;

            let fill = "rgba(255,255,255,0.02)";
            let stroke = "#FFFFFF";
            let strokeWidth = r.small ? 1 : 1.8;
            if (isCurrent) {
              fill = "rgba(230,161,53,0.14)"; stroke = "#E6A135"; strokeWidth = 2.2;
            } else if (isTarget) {
              fill = viaPortal ? "rgba(230,161,53,0.06)" : "rgba(126, 201, 138, 0.06)";
              stroke = viaPortal ? "#E6A135" : "#7EC98A";
              strokeWidth = 1.8;
            }

            return (
              <g
                key={r.id}
                style={{ cursor: clickable && !isCurrent ? "pointer" : "default" }}
                onClick={() => onRoomClick(r.id)}
                data-testid={`room-${r.id}`}
              >
                <rect x={rx} y={ry} width={rw} height={rh} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />
                <text x={rx + rw / 2} y={ry + 18} textAnchor="middle" style={{
                  fontFamily: "'JetBrains Mono', monospace", fontSize: r.small ? "9px" : "11px",
                  fill: isCurrent ? "#E6A135" : "#EAF2FF", letterSpacing: "0.15em", fontWeight: 600,
                  pointerEvents: "none",
                }}>{r.name}</text>
                {r.anchor && (
                  <text x={rx + rw / 2} y={ry + rh - 12} textAnchor="middle" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "9px", fill: "#8AB4E8", pointerEvents: "none" }}>
                    anchor · portal doubles
                  </text>
                )}
                {r.note && (
                  <text x={rx + rw / 2} y={ry + rh - 10} textAnchor="middle" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "12px", fill: "#B8CFEE", fontStyle: "italic", pointerEvents: "none" }}>
                    {r.note}
                  </text>
                )}
                {r.portal && (
                  <g pointerEvents="none">
                    <circle cx={pad + r.portal.x * cell} cy={pad + r.portal.y * cell} r="10" fill="rgba(230,161,53,0.20)" stroke="#E6A135" strokeWidth="1.5" />
                    <text x={pad + r.portal.x * cell} y={pad + r.portal.y * cell + 4} textAnchor="middle" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "10px", fill: "#E6A135", fontWeight: 700 }}>P</text>
                  </g>
                )}
                {r.trapdoor && (
                  <g pointerEvents="none">
                    <rect x={rx + rw - 32} y={ry + rh - 32} width="22" height="22" rx="3" fill="#3E8B5A" stroke="#B9E4C9" strokeWidth="1.2" />
                    <text x={rx + rw - 21} y={ry + rh - 17} textAnchor="middle" style={{ fontSize: "12px" }}>▼</text>
                    <text x={rx + rw - 21} y={ry + rh + 2} textAnchor="middle" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "8px", fill: "#B9E4C9" }}>trapdoor</text>
                  </g>
                )}
                {r.palms && (
                  <g pointerEvents="none">
                    {[[1.2, 1.2], [4.5, 1.2], [1.2, 4.5], [4.5, 4.5]].map(([px, py], i) => (
                      <g key={i}>
                        <circle cx={rx + px * cell} cy={ry + py * cell} r="9" fill="rgba(76, 175, 80, 0.15)" stroke="#7EC98A" strokeWidth="1" />
                        <text x={rx + px * cell} y={ry + py * cell + 3} textAnchor="middle" style={{ fontSize: "10px" }}>❦</text>
                      </g>
                    ))}
                  </g>
                )}
                {r.portal_arrival && (
                  <g pointerEvents="none">
                    <polygon
                      points={`${pad + r.portal_arrival.x * cell},${pad + r.portal_arrival.y * cell - 14} ${pad + r.portal_arrival.x * cell + 14},${pad + r.portal_arrival.y * cell} ${pad + r.portal_arrival.x * cell},${pad + r.portal_arrival.y * cell + 14} ${pad + r.portal_arrival.x * cell - 14},${pad + r.portal_arrival.y * cell}`}
                      fill="rgba(230,161,53,0.15)" stroke="#E6A135" strokeWidth="1.5"
                    />
                    <line x1={pad + r.portal_arrival.x * cell - 20} y1={pad + r.portal_arrival.y * cell} x2={pad + r.portal_arrival.x * cell + 20} y2={pad + r.portal_arrival.y * cell} stroke="#E6A135" strokeWidth="0.8" />
                    <line x1={pad + r.portal_arrival.x * cell} y1={pad + r.portal_arrival.y * cell - 20} x2={pad + r.portal_arrival.x * cell} y2={pad + r.portal_arrival.y * cell + 20} stroke="#E6A135" strokeWidth="0.8" />
                    <text x={pad + r.portal_arrival.x * cell} y={pad + r.portal_arrival.y * cell + 32} textAnchor="middle" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "8.5px", fill: "#E6A135" }}>PORTAL ARRIVAL</text>
                  </g>
                )}
                {r.vault && (
                  <g pointerEvents="none">
                    <g stroke="#B8CFEE" strokeWidth="0.7">
                      <line x1={rx + 8} y1={ry + 30} x2={rx + rw - 8} y2={ry + 30} />
                      <line x1={rx + 8} y1={ry + 36} x2={rx + rw - 8} y2={ry + 36} />
                      <line x1={rx + 8} y1={ry + rh - 30} x2={rx + rw - 8} y2={ry + rh - 30} />
                      <line x1={rx + 8} y1={ry + rh - 36} x2={rx + rw - 8} y2={ry + rh - 36} />
                    </g>
                    <rect x={rx + rw / 2 - 55} y={ry + rh / 2 - 20} width="110" height="42" fill="rgba(255,255,255,0.04)" stroke="#EAF2FF" strokeWidth="1.2" />
                    <text x={rx + rw / 2} y={ry + rh / 2 + 4} textAnchor="middle" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "8.5px", fill: "#EAF2FF" }}>EVIDENCE STORAGE</text>
                    <g>
                      {Array.from({ length: 12 }).map((_, i) => (
                        <circle key={i} cx={rx + rw - 40 + (i % 4) * 10} cy={ry + rh - 60 + Math.floor(i / 4) * 10} r="3.5" fill="rgba(166,50,50,0.35)" stroke="#B8CFEE" strokeWidth="0.5" />
                      ))}
                      <text x={rx + rw - 25} y={ry + rh - 25} textAnchor="middle" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "8px", fill: "#B8CFEE" }}>WINE</text>
                    </g>
                    <rect x={rx + 14} y={ry + rh - 60} width="28" height="42" fill="rgba(255,255,255,0.05)" stroke="#EAF2FF" strokeWidth="1" />
                    <text x={rx + 28} y={ry + rh - 12} textAnchor="middle" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "7.5px", fill: "#B8CFEE" }}>FILING</text>
                  </g>
                )}
              </g>
            );
          })}

          {/* tiles from backend */}
          {floor === "ground" && game.tiles.map(t => {
            const tx = pad + t.x * cell;
            const ty = pad + t.y * cell;
            const revealable = canAct && !t.revealed && active.room === t.room;
            if (t.revealed) {
              return (
                <g key={t.id} pointerEvents="none">
                  <circle cx={tx} cy={ty} r="9" fill={t.is_alarm ? "rgba(166,50,50,0.4)" : "rgba(126,201,138,0.4)"} stroke={t.is_alarm ? "#E88A8A" : "#B9E4C9"} strokeWidth="1" />
                  <text x={tx} y={ty + 4} textAnchor="middle" style={{ fontSize: "12px" }}>{t.is_alarm ? "🚨" : "✓"}</text>
                </g>
              );
            }
            return (
              <g
                key={t.id}
                style={{ cursor: revealable ? "pointer" : "default" }}
                onClick={() => onTileClick(t)}
                data-testid={`tile-${t.id}`}
              >
                <circle cx={tx} cy={ty} r={revealable ? 13 : 10} fill={revealable ? "rgba(230,161,53,0.25)" : "rgba(0,0,0,0)"} stroke={revealable ? "#E6A135" : "transparent"} strokeWidth="1" />
                <text x={tx} y={ty + 5} textAnchor="middle" style={{
                  fontSize: "17px", fill: "#EAF2FF", opacity: 0.85,
                  filter: revealable ? "drop-shadow(0 0 4px rgba(230,161,53,0.7))" : "drop-shadow(0 0 3px rgba(234,242,255,0.5))",
                }}>📜</text>
              </g>
            );
          })}

          {/* Ragnar the dog */}
          {floor === "ground" && game.ragnar && game.ragnar.active && (() => {
            const room = GROUND_ROOMS.find(r => r.id === game.ragnar.room);
            if (!room) return null;
            const rx = pad + room.x * cell;
            const ry = pad + room.y * cell;
            const rw = room.w * cell;
            const rh = room.h * cell;
            return (
              <g pointerEvents="none" data-testid="ragnar-pawn">
                <circle cx={rx + 22} cy={ry + rh - 24} r="14" fill="#5A2E1A" stroke="#B98A5C" strokeWidth="2"
                  style={{ filter: "drop-shadow(0 0 10px rgba(230,161,53,0.4))" }} />
                <text x={rx + 22} y={ry + rh - 19} textAnchor="middle" style={{ fontSize: "16px" }}>🐾</text>
                <text x={rx + 22} y={ry + rh - 5} textAnchor="middle" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "8.5px", fill: "#F0B84E", letterSpacing: "0.08em" }}>RAGNAR</text>
              </g>
            );
          })()}

          {/* player pawns */}
          {rooms.map(r => {
            const rx = pad + r.x * cell;
            const ry = pad + r.y * cell;
            const rw = r.w * cell;
            const rh = r.h * cell;
            const list = playersByRoom[r.id] || [];
            return list.map((p, i) => {
              const total = list.length;
              const spread = 20;
              const px = rx + rw / 2 + (i - (total - 1) / 2) * spread;
              const py = ry + rh - 22;
              const isActive = game.players[game.active_index].id === p.id;
              const colors = ["#E6A135", "#F4A6B8", "#6EE7C7", "#B14CB5", "#F0B84E"];
              const color = colors[game.players.indexOf(p) % colors.length];
              return (
                <g key={p.id} pointerEvents="none" style={{ transition: "transform 400ms" }}>
                  <circle cx={px} cy={py} r={isActive ? 13 : 10} fill={color} stroke="#0E0A08" strokeWidth="2"
                    style={{ filter: isActive ? `drop-shadow(0 0 8px ${color})` : "none" }}
                  />
                  <text x={px} y={py + 4} textAnchor="middle" style={{
                    fontFamily: "'JetBrains Mono', monospace", fontSize: "10px", fill: "#0E0A08", fontWeight: 700,
                  }}>{p.name.charAt(0).toUpperCase()}</text>
                </g>
              );
            });
          })}

          {floor === "ground" && (
            <g pointerEvents="none">
              <text x={pad + 15.5 * cell} y={pad + 2 * cell + 5} textAnchor="middle" style={{
                fontSize: "13px", fill: "#F4A6B8", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, letterSpacing: "0.1em",
              }}>✦SHRBT</text>
            </g>
          )}

          <g transform={`translate(${pad}, ${H - 14})`}>
            <text style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "8.5px", fill: "#8AB4E8", letterSpacing: "0.14em" }}>
              {floor === "ground"
                ? "📜 tap to reveal in your room · P portal · ▼ trapdoor · glowing rooms = you can move"
                : "◈ portal arrival · alarm-free safe zone · leave no man behind"}
            </text>
          </g>
        </svg>
      </div>

      {canAct && active.room === "scrip" && (
        <button
          className="brass-btn brass-btn-solid mt-2 w-full flex items-center justify-center gap-2"
          onClick={() => action("/trapdoor", { player_id: active.id })}
          disabled={busy}
          data-testid="trapdoor-btn"
        >
          <ChevronsDown size={13} /> Slip through the trapdoor (→ The Foyer)
        </button>
      )}
      {canAct && active.room === "foyer" && (
        <button
          className="brass-btn brass-btn-solid mt-2 w-full flex items-center justify-center gap-2"
          onClick={() => action("/trapdoor", { player_id: active.id })}
          disabled={busy}
          data-testid="trapdoor-btn"
        >
          <ChevronsDown size={13} style={{ transform: "rotate(180deg)" }} /> Climb back up (→ Scriptorium)
        </button>
      )}
    </div>
  );
}

// ============ RONDEL ============
function Rondel({ game, onTake, busy }) {
  const active = game.players[game.active_index];
  const canAct = game.phase === "turn" && !active.is_ai;
  const size = 420;
  const cx = size / 2, cy = size / 2, r = size / 2 - 12;

  return (
    <div className="leather rounded-md p-6 mb-4" data-testid="rondel">
      <div className="flex justify-between items-center mb-3">
        <div className="eyebrow">The Rondel · {game.shift_slices_taken}/{game.max_shift} this shift</div>
        {canAct && <div className="brass-tag" data-testid="rondel-prompt">Your turn — tap a slice</div>}
      </div>
      <div className="flex justify-center">
        <div className="alien-glow-wrap" style={{ width: "100%", maxWidth: 480 }} data-testid="rondel-glow">
          <svg viewBox={`0 0 ${size} ${size}`} width="100%" style={{ maxWidth: 480, display: "block" }}>
          {game.rondel.map((slc) => {
            const startAngle = (slc.index / 8) * 2 * Math.PI - Math.PI / 2 - Math.PI / 8;
            const endAngle = startAngle + (2 * Math.PI) / 8;
            const x1 = cx + r * Math.cos(startAngle);
            const y1 = cy + r * Math.sin(startAngle);
            const x2 = cx + r * Math.cos(endAngle);
            const y2 = cy + r * Math.sin(endAngle);
            const midAngle = (startAngle + endAngle) / 2;
            const labelR = r * 0.7;
            const lx = cx + labelR * Math.cos(midAngle);
            const ly = cy + labelR * Math.sin(midAngle);
            const isEmpty = slc.taken || slc.portions_left <= 0;
            const fill = isEmpty ? "#1A130E" : slc.hue;
            const opacity = isEmpty ? 0.25 : 0.85;

            return (
              <g key={slc.index}>
                <path
                  d={`M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 0 1 ${x2} ${y2} Z`}
                  fill={fill}
                  fillOpacity={opacity}
                  stroke="#0E0A08"
                  strokeWidth="2"
                  style={{
                    cursor: canAct && !isEmpty ? "pointer" : "default",
                    transition: "filter 200ms, transform 200ms",
                    filter: canAct && !isEmpty ? "drop-shadow(0 0 8px rgba(230,161,53,0.25))" : "none",
                  }}
                  onClick={() => canAct && !isEmpty && !busy && onTake(slc.index)}
                  onMouseEnter={(e) => canAct && !isEmpty && e.currentTarget.setAttribute("fill-opacity", "1")}
                  onMouseLeave={(e) => e.currentTarget.setAttribute("fill-opacity", opacity)}
                  data-testid={`slice-${slc.index}`}
                />
                <text x={lx} y={ly - 8} textAnchor="middle" style={{ fontSize: 28, pointerEvents: "none" }}>
                  {isEmpty ? "" : slc.icon}
                </text>
                <text
                  x={lx} y={ly + 14} textAnchor="middle"
                  style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", fill: isEmpty ? "#6E5C42" : "#1A1410", fontWeight: 600, pointerEvents: "none" }}
                >
                  {slc.name}
                </text>
                {slc.kind === "multi" && !isEmpty && (
                  <text
                    x={lx} y={ly + 28} textAnchor="middle"
                    style={{ fontSize: 9, fontFamily: "'JetBrains Mono', monospace", fill: "#3A2410", pointerEvents: "none" }}
                  >
                    ×{slc.portions_left}
                  </text>
                )}
              </g>
            );
          })}
          <circle cx={cx} cy={cy} r={r * 0.15} fill="#1A130E" stroke="var(--brass)" strokeWidth="2" />
          <text x={cx} y={cy + 5} textAnchor="middle" style={{ fontSize: 18, fill: "var(--brass)" }}>❦</text>
        </svg>
        </div>
      </div>
    </div>
  );
}

// ============ ACTION ROW ============
function ActionRow({ game, action, busy }) {
  const active = game.players[game.active_index];

  if (game.phase === "game-over") return null;

  if (active.is_ai && (game.phase === "turn" || game.phase === "reactions")) {
    return (
      <div className="leather rounded-md p-4 flex items-center justify-between gap-3" data-testid="ai-turn-panel">
        <div className="flex items-center gap-2">
          <Cpu size={16} style={{ color: "var(--brass)" }} className="animate-pulse" />
          <span className="mono text-sm" style={{ color: "var(--paper-warm)" }}>
            {active.name} is thinking…
          </span>
        </div>
        {game.phase === "turn" ? (
          <button className="brass-btn brass-btn-solid" onClick={() => action("/ai-turn")} disabled={busy} data-testid="ai-play-btn">
            {busy ? "…" : "Play AI Turn"} <ArrowRight size={12} className="inline ml-1" />
          </button>
        ) : (
          <button className="brass-btn brass-btn-solid" onClick={() => action("/pass")} disabled={busy} data-testid="end-turn-btn">
            End Turn <ArrowRight size={12} className="inline ml-1" />
          </button>
        )}
      </div>
    );
  }

  if (game.phase === "shift-end") {
    return (
      <div className="leather rounded-md p-4 text-center" data-testid="shift-end-panel">
        <div className="serif text-xl font-bold mb-2" style={{ color: "var(--brass)" }}>Shift {game.shift} closes</div>
        <div className="serif-italic mb-3" style={{ color: "var(--paper-warm)" }}>The dinner inquiry commences. Hands refresh to 5.</div>
        <button className="brass-btn brass-btn-solid" onClick={() => action("/pass")} disabled={busy} data-testid="advance-shift-btn">
          Begin Shift {game.shift + 1} <ArrowRight size={12} className="inline ml-1" />
        </button>
      </div>
    );
  }

  if (game.phase === "reactions" && !active.is_ai) {
    return (
      <div className="leather rounded-md p-4 flex items-center justify-between gap-3" data-testid="reactions-panel">
        <div className="mono text-sm" style={{ color: "var(--paper-warm)" }}>
          React or end turn. Non-active players may Share or Counter Slice from the side rail.
        </div>
        <button className="brass-btn brass-btn-solid" onClick={() => action("/pass")} disabled={busy} data-testid="end-turn-btn">
          End Turn <ArrowRight size={12} className="inline ml-1" />
        </button>
      </div>
    );
  }

  return null;
}

// ============ SIDE RAIL — Players + Chair + Log ============
function SideRail({ game, action, busy }) {
  return (
    <div className="space-y-4">
      <PlayerList game={game} action={action} busy={busy} />
      <VaultPanel game={game} action={action} busy={busy} />
      <ChairPanel game={game} action={action} busy={busy} />
      <LogPanel game={game} />
    </div>
  );
}

function VaultPanel({ game, action, busy }) {
  const active = game.players[game.active_index];
  const inVault = active.room === "vault";
  return (
    <div className="leather rounded-md p-4" data-testid="vault-panel">
      <div className="eyebrow mb-2 flex items-center gap-2"><Archive size={11} /> Evidence Vault ({game.vault_evidence.length}/{game.vault_cap})</div>
      <div className="space-y-1 mb-2">
        {game.vault_evidence.length === 0 && (
          <div className="mono text-xs" style={{ color: "var(--muted)" }}>No evidence filed yet.</div>
        )}
        {game.vault_evidence.map((ev, i) => (
          <div key={i} className="flex justify-between items-center text-xs mono p-1 rounded" style={{ background: "rgba(42,72,88,0.25)", color: "var(--paper-warm)" }} data-testid={`vault-item-${i}`}>
            <span>📎 {ev}</span>
            {inVault && !active.is_ai && (
              <button
                className="brass-btn text-[10px] px-2 py-0.5"
                onClick={() => action("/vault/analyse", { player_id: active.id, evidence_index: i })}
                disabled={busy}
                data-testid={`analyse-${i}`}
              >Analyse +2</button>
            )}
          </div>
        ))}
      </div>
      {game.incinerated.length > 0 && (
        <div className="mono text-[10px]" style={{ color: "var(--crimson)" }}>
          🔥 incinerated: {game.incinerated.length}
        </div>
      )}
      {inVault && !active.is_ai && active.items.length > 0 && (
        <div className="mt-3 pt-3 border-t border-[color:var(--border-brass)]">
          <div className="eyebrow mb-2" style={{ color: "var(--brass)" }}>File one of your items (+3 VP)</div>
          <div className="space-y-1">
            {active.items.map((it, i) => (
              <button
                key={i}
                className="brass-btn text-xs w-full text-left"
                onClick={() => action("/vault/deposit", { player_id: active.id, item_index: i })}
                disabled={busy}
                data-testid={`deposit-${i}`}
              >
                📎 {it}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PlayerList({ game, action, busy }) {
  const active = game.players[game.active_index];
  const lastSlice = game.last_taken_slice != null ? game.rondel[game.last_taken_slice] : null;
  const canShare = game.phase === "reactions" && lastSlice && lastSlice.kind === "multi" && lastSlice.portions_left > 0 && !game.shared_this_turn;
  const canCounter = game.phase === "reactions" && lastSlice && !game.contest;

  return (
    <div className="leather rounded-md p-4" data-testid="player-list">
      <div className="eyebrow mb-3">Players</div>
      <div className="space-y-3">
        {game.players.map((p, i) => {
          const isActive = i === game.active_index;
          const isClaimant = game.contest && game.contest.claimant_id === p.id;
          const canAnte = !!game.contest && !isActive && !isClaimant && !p.is_ai && p.dominoes.length >= 2;
          return (
            <div
              key={p.id}
              className="p-3 rounded"
              style={{
                background: isActive ? "rgba(230,161,53,0.10)" : isClaimant ? "rgba(166,50,50,0.10)" : "rgba(197,160,89,0.03)",
                border: isActive ? "1px solid var(--brass)" : isClaimant ? "1px solid var(--crimson)" : "1px solid var(--border-brass)",
              }}
              data-testid={`player-card-${i}`}
            >
              <div className="flex justify-between items-center">
                <span className="serif font-bold flex items-center gap-1" style={{ color: isActive ? "var(--brass)" : "#F0E6D2" }}>
                  {p.name} {p.is_ai && <Cpu size={11} className="inline" />}
                  {p.frozen && <span title="frozen">❄</span>}
                  {isClaimant && <span className="crimson-tag ml-1">Claimant</span>}
                </span>
                <span className="brass-tag">{p.vp} VP</span>
              </div>
              <div className="flex gap-1.5 mt-2 flex-wrap items-center" data-testid={`hand-${i}`}>
                {p.dominoes.length === 0 && <span className="mono text-xs" style={{ color: "var(--muted)" }}>empty hand</span>}
                {p.dominoes.map((d, di) => (
                  <DominoTile key={di} total={d} size={isActive ? "lg" : "sm"} data-testid={`domino-${i}-${di}`} />
                ))}
              </div>
              {p.items.length > 0 && (
                <div className="mt-2 mono text-[10px]" style={{ color: "var(--muted)" }}>
                  📦 {p.items.length} · {p.items.slice(0, 2).map(x => x.replace(/-/g, " ")).join(" · ")}{p.items.length > 2 ? " …" : ""}
                </div>
              )}
              <div className="mono text-[10px] mt-1" style={{ color: "var(--brass-dim)" }}>📍 {p.room}</div>

              {!isActive && !p.is_ai && (
                <div className="mt-2 space-y-1">
                  {canShare && p.dominoes.length >= 2 && (
                    <button
                      className="brass-btn text-xs w-full flex items-center justify-center gap-1"
                      onClick={() => action("/share", { sharer_id: p.id })}
                      disabled={busy}
                      data-testid={`share-btn-${i}`}
                    >
                      <Handshake size={11} /> Share {lastSlice.name} (2 dominoes)
                    </button>
                  )}
                  {canCounter && p.dominoes.length >= 1 && (
                    <button
                      className="brass-btn text-xs w-full flex items-center justify-center gap-1"
                      onClick={() => action("/counter", { actor_id: p.id })}
                      disabled={busy}
                      data-testid={`counter-btn-${i}`}
                    >
                      <Zap size={11} /> Counter Slice (1 domino)
                    </button>
                  )}
                  {canAnte && (
                    <button
                      className="brass-btn brass-btn-solid text-xs w-full flex items-center justify-center gap-1"
                      onClick={() => action("/ante", { actor_id: p.id })}
                      disabled={busy}
                      data-testid={`ante-btn-${i}`}
                    >
                      <Gavel size={11} /> Up the Ante (2 dominoes)
                    </button>
                  )}
                </div>
              )}
              {p.frozen && !p.is_ai && p.dominoes.length > 0 && (
                <button
                  className="brass-btn text-xs w-full mt-2 flex items-center justify-center gap-1"
                  onClick={() => action("/ragnar/bone", { donor_id: p.id })}
                  disabled={busy}
                  data-testid={`bone-btn-${i}`}
                >
                  <Bone size={11} /> Give a Dog a Bone (donate)
                </button>
              )}
              {p.point_token && p.dominoes.includes(0) && game.open_ends && game.open_ends.includes(0) && !p.is_ai && (
                <button
                  className="brass-btn brass-btn-solid text-xs w-full mt-2 flex items-center justify-center gap-1"
                  style={{ background: "linear-gradient(180deg, var(--brass), #7A5A2A)" }}
                  onClick={() => action("/point-blank/play", { player_id: p.id })}
                  disabled={busy}
                  data-testid={`play-point-blank-${i}`}
                >
                  <Target size={11} /> Retire the Point — play [0|0]
                </button>
              )}
              {p.point_token && (
                <div className="mt-1 mono text-[10px]" style={{ color: "var(--brass)" }}>
                  🎯 Holding the Point — {p.dominoes.includes(0) ? (game.open_ends?.includes(0) ? "play now on a [0] end" : "waiting for a [0] end") : "…"}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ChairPanel({ game, action, busy }) {
  const active = game.players[game.active_index];
  return (
    <div className="leather rounded-md p-4" data-testid="chair-panel">
      <div className="eyebrow mb-2 flex items-center gap-2"><Hammer size={11} /> The Chair</div>
      <div className="flex items-center gap-2 mb-2">
        {[0, 1, 2, 3].map(i => (
          <div
            key={i}
            className="flex-1 h-6 rounded"
            style={{
              background: i < game.chair_dominoes ? "linear-gradient(180deg, var(--brass), #9E7F3A)" : "#0F0A07",
              border: "1px solid var(--border-brass)",
            }}
          />
        ))}
      </div>
      <div className="mono text-xs mb-3" style={{ color: "var(--muted)" }}>
        {game.chair_built ? "🪑 Ready to claim." : `${game.chair_dominoes} / 4 dominoes donated`}
      </div>
      {game.chair_available_claim ? (
        <button
          className="brass-btn brass-btn-solid w-full"
          onClick={() => action("/chair/claim")}
          disabled={busy}
          data-testid="chair-claim-btn"
        >
          {active.name}, claim buff (+2 VP, fresh domino)
        </button>
      ) : !game.chair_built ? (
        <div className="space-y-1">
          {game.players.map((p, pi) => (
            p.dominoes.length > 0 && !p.is_ai && (
              <button
                key={p.id}
                className="brass-btn text-xs w-full"
                onClick={() => action("/chair/donate", { player_id: p.id, domino_index: Math.floor(Math.random() * p.dominoes.length) })}
                disabled={busy}
                data-testid={`donate-btn-${pi}`}
              >
                Donate random from {p.name}
              </button>
            )
          ))}
        </div>
      ) : null}
    </div>
  );
}

function LogPanel({ game }) {
  return (
    <div className="leather rounded-md p-4" data-testid="log-panel">
      <div className="eyebrow mb-2">Case Log</div>
      <div className="space-y-1 max-h-64 overflow-y-auto mono text-xs" style={{ color: "var(--paper-warm)" }}>
        {game.log.slice().reverse().map((line, i) => (
          <div key={i} style={{ opacity: 1 - i * 0.04 }}>{line}</div>
        ))}
      </div>
    </div>
  );
}

// ============ GAME OVER ============
function GameOver({ game, onReset }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" data-testid="game-over-modal">
      <div className="paper rounded-md p-10 max-w-md w-full text-center">
        <div className="eyebrow" style={{ color: "var(--crimson-deep)" }}>Case Closed</div>
        <h3 className="serif text-4xl font-bold mt-2 mb-4" style={{ color: "var(--paper-ink)" }}>{game.winner}</h3>
        <p className="serif-italic text-lg mb-6" style={{ color: "var(--paper-ink)" }}>
          takes the inquiry with the highest VP.
        </p>
        <div className="mono text-xs space-y-1 mb-6" style={{ color: "var(--paper-ink)" }}>
          {game.players.map(p => (
            <div key={p.id} className="flex justify-between">
              <span>{p.name}</span>
              <strong>{p.vp} VP</strong>
            </div>
          ))}
        </div>
        <button className="brass-btn brass-btn-solid w-full" onClick={onReset} data-testid="game-over-reset">
          Deal a new inquiry
        </button>
      </div>
    </div>
  );
}
